/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import React, { Fragment, useEffect } from 'react'
import { observer } from 'mobx-react'
import useRootStore from '@store/useRootStore'
import { useOnMount, useOnUnmount } from '@utils/reactExt'
// img
import { CircleDownIco } from '@shared/Icon/ArrowIcon'
// css
import { DropdownWrap, preventScrollStyle, TotalTabWrap } from '@styles/base.style'
import { FilterStyle, FilterWrapStyle, MatchChart, PickList } from '@views/Home/index.style'
import { message, Select } from 'antd'
import { PAGE_TYPE } from '@interface/common'
import { ListView } from 'antd-mobile'
import { AdvButton } from '@components/Button'
import * as _ from 'lodash'
import FilterArrowIco from '../../../../shared/Icon/EtcIcon/FilterArrowIco'
import { isMobile } from 'react-device-detect'
import { AdvEmpty } from '@components/AdvEmpty'
import { EMPTY_ICON } from '@constants/index'
import DialogUtils from '@utils/dialog-utils'
import { USER_PICK_ORDER_TYPE } from '@v2/constants/user-pick'
import { Schema$UserPick, Schema$UserPickExtend } from '@interface/user-pick'
import { COMMUNICATION_BLOCK_TYPE, EXCEPTIONAL_UNIQUE_ID, FROM_VIEW_TYPE } from '@v2/constants/common'
import { UserPickWriteButton } from '@views/Home/UserPick/Components/UserPickWriteButton'
import { UserPickItem } from '@views/Home/UserPick/Components/UserPickItem'
import { UserPickMatchInfo } from '@views/Home/UserPick/Components/UserPickMatchInfo'
import { FavoriteButton } from '@components/Button/FavoriteButton'
import * as Utils from '@utils/utils'
import { UNIFIED_REGULAR_ODDS } from '@game-type/game-market-types'
import StorageUtils from '@utils/storage-utils'
import { runInAction } from 'mobx'
import { LeagueTab } from '@views/Matchup/Tab/LeagueTab'

const FAVORITE_ACTIVE_CLASS = 'active ani'

const dataSource = new ListView.DataSource({
  rowHasChanged: (row1, row2) => row1 !== row2,
})

const { Option } = Select

export interface Props {
  favoriteMatchCallback?: (isFavorite) => void
  targetUserPickId?: string,
  fromView?: FROM_VIEW_TYPE
  isTodayUserPick?: boolean
}

const ListViewStyle = css`
  height: 100%;
  overflow-x: hidden !important;
  ${preventScrollStyle};

  .am-list-body {
    margin-bottom: 90px;
  }
`

/**
 * 경기 분석 팝업 컨텐츠(매치정보, 승무배 베팅정보, 분석글 목록)
 */
export const UserPickContents: React.FC<Props> = observer((props) => {
  const { userPickStore, matchupStore } = useRootStore()
  const { favoriteMatchCallback, fromView, isTodayUserPick, targetUserPickId } = props
  const { majorUniqueID } = matchupStore

  const {
    selectedUserPickMatch,
    hasMoreUserPicks,
    userPicks,
    userPickMarkets,
    userPickMatchWayRatio,
    selectedEtcMarket,
    userPickMarketFilter,
    userPickRecentHitRateMap,
    fetchUserPickMatchDetailOrder,
    activeUserFilter,
  } = userPickStore
  if (!selectedUserPickMatch) {
    return null
  }
  const {
    matchId,
    sportId,
    isFavoriteMatch,
    dateOfMatch,
  } = selectedUserPickMatch

  useOnMount(() => {
    StorageUtils.savePickPromotionButtonHide()
  })

  useOnUnmount(() => {
    userPickStore.resetMatchDetail()
    userPickStore.setSelectedUserPickMatch(null)
  })

  useEffect(() => {
    fetchUserPickMatchDetailPackage()
  }, [userPickMarketFilter])

  useEffect(() => {
    // activeUserFilter 는 특정 사용자 게시글만 경기 상세 화면에 표시하는 기능의 활성화를 의미한다.
    // 아래 조건에서 return 되는 시나리오는 다음과 같다.
    // 1. 마이 페이지 - 작성한 댓글 - 경기 분석 진입 시
    // 2. 상세 BET - 스포츠톡 - 경기 분석 진입 시
    // 이곳에서 return 된 후 탭 초기화 시점에 userPickMarketFilter useEffect 가 동작하여 경기 상세 데이터를 요청한다.
    if (activeUserFilter || _.isNil(activeUserFilter)) {
      userPickStore.setUserPickMarketFilter(EXCEPTIONAL_UNIQUE_ID.TOTAL_USER_PICK)
      return
    }

    // activeUserFilter false 가 된 시점에 데이터를 재로드를 해야한다.
    // activeUserFilter true 시에 userPickMarketFilter 값을 EXCEPTIONAL_UNIQUE_ID.TOTAL_USER_PICK 을 사용하며
    // activeUserFilter false 시에도 userPickMarketFilter 값을 동일하게 사용하기 때문에
    // 해당 useEffect 로 데이터를 갱신 하도록 구현했다.
    fetchUserPickMatchDetailPackage()
  }, [activeUserFilter])

  const fetchUserPickMatchDetailPackage = (): void => {
    if (userPickMarketFilter) {
      userPickStore.setSelectedEtcMarket(undefined)
      userPickStore.setFetchUserPickMatchDetailPageNum(0)
      userPickStore.setFetchUserPickMatchDetailOrder(USER_PICK_ORDER_TYPE.RECOMMEND)

      const opts = {
        matchId,
      }

      if (activeUserFilter) {
        opts['targetUserPickId'] = targetUserPickId
      }
      userPickStore.fetchUserPickMatchDetailPackage(opts).catch(console.error)
    }
  }

  const favoriteMatchButtonClass = `${isFavoriteMatch ? FAVORITE_ACTIVE_CLASS : ''}`
  const onClickFavoriteMatch = async () => {
    if (!Utils.isNotStartedMatch(dateOfMatch)) {
      message.info('현재 찜할 수 없는 경기입니다.')
      return
    }
    const result = await matchupStore.toggleFavoriteMatch({ matchId, sportId, dateOfMatch })
    userPickStore.updateMatchFavorite(result, matchId, fromView)
    if (_.isFunction(favoriteMatchCallback)) {
      favoriteMatchCallback(result)
    }
  }

  const onClickSelectType = () => {
    const etcMarkets = _.filter(userPickMarkets, m => {
      return !UNIFIED_REGULAR_ODDS.includes(m.marketType.toString())
    })
    DialogUtils.showUserPickEtcMarketFilterList(etcMarkets, matchId)
  }

  const onChangeUserPickListOrder = (value: USER_PICK_ORDER_TYPE): void => {
    userPickStore.setFetchUserPickMatchDetailOrder(value)
    userPickStore.setFetchUserPickMatchDetailPageNum(0)
    userPickStore.fetchUserPickMatchDetailPackage({ matchId }).catch(console.error)
  }

  const onUserBlockSuccessHandler = (userPromiseId: string, blockTypes: COMMUNICATION_BLOCK_TYPE[]) => {
    runInAction(() => {
      const isUserBlock = !_.isEmpty(blockTypes)
      const isUserPickBlock = _.includes(blockTypes, COMMUNICATION_BLOCK_TYPE.USER_PICK)

      userPickStore.userPicks = userPickStore.setUserBlock(
        userPicks as Schema$UserPickExtend[],
        userPromiseId,
        isUserBlock,
        isUserPickBlock,
      )
    })
  }

  const renderRow = (row: Schema$UserPick) => {
    const recentHitRate = userPickRecentHitRateMap?.[row.pickOwner.userPromiseId]
    const disableOverText = fromView === FROM_VIEW_TYPE.MY_PROFILE_COMMENT
      || fromView === FROM_VIEW_TYPE.SPORT_TALK
      || fromView === FROM_VIEW_TYPE.MATCHUP_DETAIL_CHEER_FOR_TEAM
    return <UserPickItem
      userPick={row}
      recentHitRate={recentHitRate}
      disableOverText={disableOverText}
      onUserBlockSuccessHandler={onUserBlockSuccessHandler}
    />
  }

  const renderFooter = () => {
    let footer
    if (_.size(userPicks) > 0) {
      footer = <Fragment/>
    } else {
      footer = <AdvEmpty ico={EMPTY_ICON.USER_PICK_LIST_ITEM}/>
    }
    return <div css={{ textAlign: 'center' }}>{footer}</div>
  }

  const endReachedHandler = async () => {
    if (hasMoreUserPicks) {
      userPickStore.fetchUserPicks(matchId).catch(console.error)
    }
  }

  const winRatio = userPickMatchWayRatio?.home ?? 0
  const drawRatio = userPickMatchWayRatio?.draw ?? 0
  const loseRatio = userPickMatchWayRatio?.away ?? 0

  let marketFilterClassName = ''
  if (majorUniqueID === EXCEPTIONAL_UNIQUE_ID.STARTED_USER_PICK
    || majorUniqueID === EXCEPTIONAL_UNIQUE_ID.CLOSED_USER_PICK) {
    if (isMobile) {
      marketFilterClassName = 'started-market-filter'
    } else {
      marketFilterClassName = 'started-market-filter pc-filter'
    }
  }

  const renderHeader = () => (
    <Fragment>
      <div>
        <UserPickMatchInfo matchInfo={selectedUserPickMatch} large/>
        <FavoriteButton
          id="userPickDetailFavoriteBtn"
          className={favoriteMatchButtonClass}
          onClick={() => onClickFavoriteMatch()}
        />
      </div>
      {(winRatio > 0 || drawRatio > 0 || loseRatio > 0) && (
        <MatchChart>
          <div className="chart-bar">
            {winRatio > 0 && <span className="chart-item-value win" style={{ flex: winRatio }}/>}
            {drawRatio > 0 && <span className="chart-item-value draw" style={{ flex: drawRatio }}/>}
            {loseRatio > 0 && <span className="chart-item-value lose" style={{ flex: loseRatio }}/>}
          </div>
          <div className="chart-info">
            {winRatio > 0 && <p className="chart-info-name win">승 {winRatio}%</p>}
            {drawRatio > 0 && <p className="chart-info-name draw">무 {drawRatio}%</p>}
            {loseRatio > 0 && <p className="chart-info-name lose">패 {loseRatio}%</p>}
          </div>
        </MatchChart>
      )}
      {_.isEmpty(targetUserPickId) && (
        <TotalTabWrap round className={isMobile ? 'auto-width' : 'auto-width show-scroll'}>
          <LeagueTab pageType={PAGE_TYPE.USER_PICK_DETAIL}/>
        </TotalTabWrap>
      )}

      {_.size(userPicks) > 0 &&
        <PickList className={activeUserFilter ? 'adv-hidden' : 'user-pick-detail-list'}>
          <div css={FilterWrapStyle}>
            {/* 마켓타입 필터 버튼이 사라져도 정렬필터 위치 유지를 위해 row-reverse 적용 */}
            {/* 정렬필터 */}
            {_.isEmpty(targetUserPickId) && (
              <DropdownWrap id={`userPickSelect:${userPickMarketFilter}`} className="user-pick">
                <Select
                  defaultValue={USER_PICK_ORDER_TYPE.RECOMMEND}
                  dropdownClassName="dark-dropdown"
                  onChange={onChangeUserPickListOrder}
                  value={fetchUserPickMatchDetailOrder}
                  getPopupContainer={() => document.getElementById(`userPickSelect:${userPickMarketFilter}`)}
                >
                  <Option value={USER_PICK_ORDER_TYPE.RECOMMEND}>추천순</Option>
                  <Option value={USER_PICK_ORDER_TYPE.NEWEST}>최신순</Option>
                  <Option value={USER_PICK_ORDER_TYPE.LIKED}>좋아요순</Option>
                </Select>
                <FilterArrowIco/>
              </DropdownWrap>
            )}

            {majorUniqueID === EXCEPTIONAL_UNIQUE_ID.CLOSED_USER_PICK &&
              <ul className="state-list">
                <li className="state-list--item">
                  <span className="circle success"/>
                  적중
                </li>
                <li className="state-list--item">
                  <span className="circle fail"/>
                  미적중
                </li>
                <li className="state-list--item">
                  <span className="circle cancel"/>
                  적특
                </li>
              </ul>
            }

            {/* 마켓타입 필터 버튼 */}
            {_.isEmpty(targetUserPickId) && userPickMarketFilter === EXCEPTIONAL_UNIQUE_ID.ETC_USER_PICK && (
              <AdvButton
                darkgray
                flex
                alignCenter
                spaceBetween
                minWidth={68}
                height={34}
                padding={'0 6px 0 14px'}
                borderRadius={18}
                rightIco
                ico={<CircleDownIco/>}
                icoSize={21}
                text={[`${selectedEtcMarket?.marketName ?? '전체'}`]}
                onClick={onClickSelectType}
                className={marketFilterClassName}
                css={FilterStyle}
              />
            )}
          </div>
        </PickList>
      }
    </Fragment>
  )

  return (
    <Fragment>
      <ListView
        css={ListViewStyle}
        dataSource={dataSource.cloneWithRows(userPicks ?? [])}
        renderHeader={!isTodayUserPick && renderHeader}
        renderRow={renderRow}
        scrollRenderAheadDistance={500}
        renderFooter={renderFooter}
        onEndReached={endReachedHandler}
        onEndReachedThreshold={10}
        initialListSize={9}
        pageSize={9}
      />
      {Utils.isNotStartedMatch(selectedUserPickMatch.dateOfMatch)
        && _.isEmpty(targetUserPickId) && !isTodayUserPick && (
          <UserPickWriteButton
            fixed
            showBanner
            className="no-nav"
            pageType={PAGE_TYPE.USER_PICK_DETAIL}
            fromView={FROM_VIEW_TYPE.USER_PICK_PICK_MATCH}
          />
        )}
    </Fragment>
  )
})
